import React, { useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import logo from "../Header/nav-logo.svg";
import { scroller } from "react-scroll";
import { Link } from "react-router-dom";
import Countdown from "react-countdown";
import { Carousel } from "react-responsive-carousel";
import { OverviewContext } from "../../store/OverviewContext";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS / PURCHASE-FUNNEL SURFACE.** The doors-open "Last Chance to
 * Join" hero: the doors countdown (`overview.doors.until`) and the `/buy`
 * checkout CTA. The conversion is styling-only — the `<Countdown>` props
 * (`date`, `renderer`, `zeroPadTime`, `daysInHours`), the `days > 0` column
 * arithmetic, the `/buy` and `/login` routes and the scroll anchor are all
 * byte-identical.
 *
 * Batch conventions (documented in full at the head of `FreeTrial/Top.tsx`):
 * `theme.spacing()` kept verbatim inside `styled()`, written as the literal px
 * string inside `sx`; `600`/`960` media queries become breakpoint helpers;
 * `769px` is not a breakpoint on this scale and stays a literal query; empty
 * `@media` blocks are dropped as dead rules (guide §1.2 row 1).
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `countdown`,
 * `countdownUnitFirstWrapper`, `noUnderline` and `dashboardScrollerHalf` were
 * declared in the v4 `useStyles` object but never referenced from the JSX, so
 * they rendered nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare host elements, which have no `sx` prop
 * ---------------------------------------------------------------------- */

// was: useStyles().background
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  width: "auto",
  display: "flex",
  boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.8)",
  height: "auto",
  paddingBottom: theme.spacing(0),
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
  },
  "@media (min-width:769px)": {
    height: "auto",
  },
  [theme.breakpoints.up("md")]: {
    height: "100vh",
  },
}));

// was: useStyles().logo
const Logo = styled("img")(({ theme }) => ({
  width: "40%",
  [theme.breakpoints.up("sm")]: {
    width: "200px",
  },
}));

// was: useStyles().skinWrapper
const SkinWrapper = styled("div")({
  position: "relative",
  marginBottom: "-3px",
});

// was: useStyles().appImage
const AppImage = styled("img")(({ theme }) => ({
  minHeight: "500px",
  maxHeight: "700px",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().dashboardScroller
const DashboardScroller = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "64px",
  left: "-1px",
  height: "378px",
  width: "258px",
  padding: "7%",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    width: "296px",
    top: "95px",
    left: "22px",
    padding: "0",
    height: "580px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().loginBut
const loginButSx = (theme) => ({
  position: "absolute",
  top: "15px",
  right: "10px",
  border: "2px solid #fff",
  color: "#fff",
  transition: "none",
  "&:hover": {
    border: "2px solid #ddd",
    color: "#ddd",
    transition: "none",
  },
  [theme.breakpoints.up("sm")]: {
    right: "15px",
  },
});

// was: useStyles().centerBoxLeft
const centerBoxLeftSx = (theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "80px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "80px",
  },
  // PARITY RESTORE — v4's `@media (min-width:769px) { height: auto }` is dropped,
  // not ported. In an `sx` object it cannot keep its v4 position: MUI's
  // `styleFunctionSx` seeds its output with an empty scaffold of the theme's
  // OWN breakpoint keys, so every `theme.breakpoints.*` block sits at the front
  // of the emitted object and any literal `@media` string is appended AFTER it.
  // 769 therefore landed after the 960 block and its `height: auto` beat
  // `height: 100vh` at every desktop width, collapsing the hero box and — because
  // it is `justifyContent: center` — riding the whole hero up the page. Measured
  // on the identical construct in `FreeTrial/Top.tsx` at 1440: the hero Grid item
  // was 700px instead of 900px and the hero sat ~98.5px too high.
  // The block is provably inert in v4: below 769 it does not apply; from 769 to
  // 959 it re-asserts the `height: auto` the flat rule and the `sm` block
  // already set; from 960 up v4's later `md` block overrode it. Dropping it
  // reproduces v4 exactly at every width (guide §1.2 row 1).
  // NB the sibling `Background` styled() above keeps its 769 block verbatim —
  // `styled` is raw emotion with no scaffold, so source order survives there.
  [theme.breakpoints.up("md")]: {
    height: "100vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
});

// was: useStyles().centerBoxRight
const centerBoxRightSx = (theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "16px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "32px",
  },
  // PARITY RESTORE — same dropped-`@media (min-width:769px)` case as
  // `centerBoxLeftSx` above; see the note there for the measurement and proof.
  [theme.breakpoints.up("md")]: {
    height: "100vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
});

// was: useStyles().title
const titleSx = (theme) => ({
  marginTop: "24px",
  letterSpacing: "-2px",
  fontSize: "5.4rem",
  lineHeight: "4.4rem",
  fontWeight: "600",
  textShadow: "-2px 2px 2px #171717",
  // color: "#e52d2d",
  // PARITY RESTORE — the responsive `& span` rule is nested INSIDE the `& span`
  // block rather than left in the `sm` block where v4 declared it. Same
  // `styleFunctionSx` scaffold as the `centerBox*` note above: breakpoint keys
  // are emitted first, so a plain nested selector written at the top level of an
  // `sx` object comes out AFTER `@media { & span }` and, being an equal-
  // specificity (0,1,1) rule later in the sheet, wins at every width — the
  // subheading renders at its xs size on desktop (measured on the identical
  // construct in `FreeTrial/Top.tsx`: 48px/51.2px instead of 64px/67.2px).
  // Nesting the breakpoint under the selector emits exactly v4's two rules in
  // v4's order, so the cascade no longer depends on key order at all.
  "& span": {
    fontSize: "3rem",
    lineHeight: "3.2rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "4rem",
      lineHeight: "4.2rem",
    },
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "7.4rem",
    lineHeight: "6.6rem",
  },
});

// was: useStyles().subTitle
const subTitleSx = (theme) => ({
  // color: "#e52d2d",
  letterSpacing: "-2px",
  fontSize: "3rem",
  lineHeight: "3.2rem",
  fontWeight: "600",
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "4rem",
    lineHeight: "4.2rem",
  },
});

// was: useStyles().lockText
// The v4 rule's two `@media` blocks held only a comment and nothing at all;
// they emitted no declarations, so they are dropped rather than ported.
const lockTextSx = {
  color: "#e52d2d",
  textAlign: "left",
  fontSize: "2rem",
  lineHeight: "2.2rem",
  marginTop: "8px",
  marginLeft: 0,
  fontWeight: "600",
};

// was: useStyles().button
// `color: "text.primary"` is NOT in the v4 rule — it restores the v4 Button
// default colour that v5+ removed (guide §2, "Button default color"). This is
// the "Join Now" checkout CTA; without the restore it renders lime-on-lime.
const buttonSx = (theme) => ({
  marginTop: "16px",
  width: "100%",
  fontSize: "1.2rem",
  padding: "16px",
  marginLeft: 0,
  marginRight: 0,
  color: "text.primary",
  [theme.breakpoints.up("sm")]: {
    width: "350px",
  },
});

// was: useStyles().buttonSubText — key order preserved (the media query was
// declared before the `& span` block in v4, and emotion emits in key order).
const buttonSubTextSx = (theme) => ({
  textAlign: "center",
  marginTop: "8px",
  marginBottom: "24px",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    width: "350px",
  },
  "& span": {
    fontWeight: "600",
    textDecoration: "underline",
    "&:hover": {
      cursor: "pointer",
    },
  },
});

// was: useStyles().countdownContainer
const countdownContainerSx = (theme) => ({
  padding: "8px 0px",
  [theme.breakpoints.up("sm")]: {
    padding: 0,
  },
});

// was: useStyles().countdownWrapper
const countdownWrapperSx = (theme) => ({
  marginTop: "8px",
  marginBottom: "4px",
  [theme.breakpoints.up("sm")]: {
    marginTop: "16px",
    marginBottom: "8px",
  },
});

// was: useStyles().countdownLabelWrapper
const countdownLabelWrapperSx = {
  marginTop: "8px",
  marginBottom: "8px",
};

// was: useStyles().countdownUnitWrapper
// `borderRadius: 5` becomes the literal "5px": sx's `borderRadius` transforms
// through `shape.borderRadius` (4 on this theme), so a bare 5 would be 20px —
// a silent 4× inflation with no type error (guide §2, T2/signin).
const countdownUnitWrapperSx = (theme) => ({
  textAlign: "center",
  // backgroundColor: "rgba(5,5,5,0.9)",
  backgroundColor: "rgba(82,82,82,0.9)",
  padding: "2% 0",
  fontSize: "2.4rem",
  fontWeight: "700",
  color: "#eeeeee",
  maxWidth: "23%",
  margin: "0 0.7%",
  borderRadius: "5px",
  [theme.breakpoints.up("sm")]: {
    padding: "1.8% 0",
    fontSize: "2rem",
    fontWeight: "700",
    maxWidth: "100px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "2% 0",
    fontSize: "2.2rem",
    fontWeight: "700",
  },
});

// was: useStyles().countdownUnitLabel
const countdownUnitLabelSx = (theme) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  textAlign: "center",
  color: "common.white",
  maxWidth: "23%",
  margin: "0 0.7%",
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
    maxWidth: "100px",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1rem",
  },
});

// was: useStyles().imageContainer
const imageContainerSx = (theme) => ({
  height: "365px",
  [theme.breakpoints.up("sm")]: {
    height: "515px",
  },
});

const Top = () => {
  const theme = useTheme();
  const { overview } = useContext(OverviewContext);

  // v4's <Hidden> was removed in v5+. Its default implementation was "js",
  // which UNMOUNTS the subtree rather than hiding it with CSS, so the faithful
  // port is useMediaQuery, with the same polarity (Hidden renders null when its
  // query matches). This file has a single, independent <Hidden xsDown>, so it
  // gets exactly one query — the complementary-pair rule (guide §2, T1/header)
  // does not apply here.
  //
  // API delta: v4's `down(key)` meant "up to the START of the NEXT breakpoint",
  // so `xsDown` === `max-width: 599.95px` === `!up("sm")`. With no matchMedia
  // (jsdom / prerender) `smUp` is false and the subtree is omitted, which is
  // what v4's `withWidth()` fallback did too.
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  function scrollToGuaranteeSection() {
    scroller.scrollTo("guarantee-section", {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
    });
  }

  const RenderCountdown = ({ days, hours, minutes, seconds }) => {
    return (
      <>
        <Typography variant="body1" sx={lockTextSx}>
          Membership Locks In:
        </Typography>
        <Container sx={countdownContainerSx}>
          {/* v5+ Grid: the `item` prop is gone and the breakpoint props are
              collapsed into `size`. Neither container sets `spacing`, so the
              computed widths are identical to v4's. */}
          <Grid container sx={countdownWrapperSx}>
            {days > 0 && (
              <Grid size={{ xs: 3, sm: 3, md: 3 }} sx={countdownUnitWrapperSx}>
                <span>
                  {days.toString().length === 1 ? "0" : ""}
                  {days}
                </span>
              </Grid>
            )}
            <Grid
              size={{
                xs: days > 0 ? 3 : 4,
                sm: days > 0 ? 3 : 4,
                md: days > 0 ? 3 : 4,
              }}
              sx={countdownUnitWrapperSx}
            >
              <span>
                {hours.toString().length === 1 ? "0" : ""}
                {hours}
              </span>
            </Grid>
            <Grid
              size={{
                xs: days > 0 ? 3 : 4,
                sm: days > 0 ? 3 : 4,
                md: days > 0 ? 3 : 4,
              }}
              sx={countdownUnitWrapperSx}
            >
              <span>
                {minutes.toString().length === 1 ? "0" : ""}
                {minutes}
              </span>
            </Grid>
            <Grid
              size={{
                xs: days > 0 ? 3 : 4,
                sm: days > 0 ? 3 : 4,
                md: days > 0 ? 3 : 4,
              }}
              sx={countdownUnitWrapperSx}
            >
              <span>
                {seconds.toString().length === 1 ? "0" : ""}
                {seconds}
              </span>
            </Grid>
          </Grid>
          <Grid container sx={countdownLabelWrapperSx}>
            {days > 0 && (
              <Grid size={{ xs: 3, sm: 3, md: 3 }} sx={countdownUnitLabelSx}>
                {days > 0 ? "Days" : "Day"}
              </Grid>
            )}
            <Grid
              size={{
                xs: days > 0 ? 3 : 4,
                sm: days > 0 ? 3 : 4,
                md: days > 0 ? 3 : 4,
              }}
              sx={countdownUnitLabelSx}
            >
              Hours
            </Grid>
            <Grid
              size={{
                xs: days > 0 ? 3 : 4,
                sm: days > 0 ? 3 : 4,
                md: days > 0 ? 3 : 4,
              }}
              sx={countdownUnitLabelSx}
            >
              Mins
            </Grid>
            <Grid
              size={{
                xs: days > 0 ? 3 : 4,
                sm: days > 0 ? 3 : 4,
                md: days > 0 ? 3 : 4,
              }}
              sx={countdownUnitLabelSx}
            >
              Secs
            </Grid>
          </Grid>
        </Container>
        <Link to="/buy" style={{ textDecoration: "none" }}>
          <Button sx={buttonSx}>Join Now</Button>
        </Link>
        <Typography variant="body2" sx={buttonSubTextSx}>
          Includes FarmFLiX Guarantee.{" "}
          <span onClick={scrollToGuaranteeSection}>What&apos;s this?</span>
        </Typography>
      </>
    );
  };

  return (
    <>
      {overview && (
        <>
          <Background className="banner-bg">
            {/* Login button */}
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Button variant="outlined" sx={loginButSx} size="small">
                Log In
              </Button>
            </Link>
            <Container maxWidth="lg" fixed>
              <Grid container>
                <Grid size={{ xs: 12, md: 7 }} sx={centerBoxLeftSx}>
                  <Box>
                    <Box>
                      <Logo src={logo} alt="FarmFLiX Logo" />
                      <Typography sx={titleSx}>Last Chance</Typography>
                      <Typography sx={subTitleSx}>To Join</Typography>
                      <Countdown
                        date={Date.parse(overview.doors.until)}
                        renderer={RenderCountdown}
                        zeroPadTime={2}
                        daysInHours={true}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }} sx={centerBoxRightSx}>
                  <SkinWrapper>
                    {/* Visible SM, MD, L, XL — was <Hidden xsDown> */}
                    {smUp && (
                      <>
                        <AppImage
                          src={
                            "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_100.png"
                          }
                          alt="FarmFLiX App"
                        />
                        <DashboardScroller>
                          <Carousel
                            useKeyboardArrows={true}
                            showArrows={false}
                            interval={3000}
                            showThumbs={false}
                            infiniteLoop={true}
                            showStatus={false}
                            autoPlay={true}
                            showIndicators={false}
                            emulateTouch={false}
                            swipeable={false}
                            transitionTime={500}
                          >
                            <Box sx={imageContainerSx}>
                              <img
                                src={
                                  "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/1.png"
                                }
                                alt="App Dashboard"
                              />
                            </Box>
                            <Box sx={imageContainerSx}>
                              <img
                                src={
                                  "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/2.png"
                                }
                                alt="App Currently Playing"
                              />
                            </Box>
                            <Box sx={imageContainerSx}>
                              <img
                                src={
                                  "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/4.png"
                                }
                                alt="Clydes Farm Series"
                              />
                            </Box>
                          </Carousel>
                        </DashboardScroller>
                      </>
                    )}
                  </SkinWrapper>
                </Grid>
              </Grid>
            </Container>
          </Background>
        </>
      )}
    </>
  );
};

export default Top;
